import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOptionalAuthContext } from '@/contexts/AuthContext';
import { useAccountPlan, useAccountUsage } from '@/hooks/useAccountUsage';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useProfile } from '@/hooks/useProfile';
import { useRequiresPlan } from '@/hooks/useRequiresPlan';
import { trackEvent } from '@/lib/analytics';
import { endpointsApi } from '@/services/api';
import { docsGuideLink } from './HelpMenu';

/** The docs-site slug of "Your First Chat with AI", the ready-made chat tutorial. */
const SIMPLE_CHAT_TUTORIAL_SLUG = 'innovate-app-simple-chat-tutorial';

type GreetingReason = 'no_plan' | 'free_no_service' | 'no_service';

/**
 * The first-conversation greeting for a new account (task 1053).
 *
 * An empty conversation renders nothing (task 769) — right for an established
 * User, wrong for a brand-new account, whose first sight would be an open,
 * silent pane whose placeholder invites the one thing a planless account
 * cannot do. So when the account is new this says one short, role-aware thing
 * at the top of the stream.
 *
 * Entirely client-side: no Assistant call, no persisted turn, nothing in the
 * thread history, and nothing the provider's unread mark can see. The sidebar
 * mounts it only while the conversation has no turns, so it is gone the
 * moment the first one exists.
 *
 * "New" is either:
 *   1. no active plan (`useRequiresPlan()`) — the rare no-subscription state,
 *      since every new account starts on Free (reason `no_plan`), or
 *   2. a plan but no AI Service yet — the usage summary reports zero deployed
 *      endpoints AND the endpoints list is empty. The Free plan gets its own
 *      sentence (reason `free_no_service`); any other plan gets the
 *      build-oriented one (reason `no_service`). The plan slug is compared
 *      case-insensitively, the same way PlanBadge reads it.
 * Every input must have answered first, so an established account never sees
 * the greeting flash while its queries are in flight.
 *
 * Each sentence must be true when it shows (task 1067): "You're on Free" only
 * for a Free account, "no plan yet" only for an account without one.
 */
export function AssistantGreeting() {
  const requiresPlan = useRequiresPlan();
  const { plan, isResolved: planResolved } = useAccountPlan();
  const { data: usage } = useAccountUsage();
  // Billing affordances follow the same owner cohort the Dashboard uses
  // (task 692): the flag IS the can-manage-billing check.
  const canManageBilling = useFlag('innovate.fe.billing.page');

  // The service lookup is only worth making when the usage summary already
  // says nothing is deployed. Same key shape as `useEndpoints`, so a
  // service-list invalidation after a build refreshes it too.
  const needsServiceLookup =
    !requiresPlan && planResolved && Boolean(plan) && usage?.deployedEndpoints === 0;
  const { data: endpointsPage } = useQuery({
    queryKey: ['endpoints', 1, 1, undefined],
    queryFn: () => endpointsApi.getAll(1, 1).then((res) => res.data),
    enabled: needsServiceLookup,
  });
  const hasNoService = needsServiceLookup && endpointsPage?.data?.length === 0;

  const isFreePlan = plan?.toLowerCase() === 'free';

  const reason: GreetingReason | null = requiresPlan
    ? 'no_plan'
    : hasNoService
      ? isFreePlan
        ? 'free_no_service'
        : 'no_service'
      : null;

  // The same name the Dashboard greets with (task 596): the profile's
  // displayName, which AMS already falls back to the email local part. The
  // token's email covers the moment before the profile answers.
  const { data: profile } = useProfile();
  const email = useOptionalAuthContext()?.user?.email;
  const name = profile?.displayName || email?.split('@')[0] || '';
  const hi = name ? `Hi ${name}.` : 'Hi.';

  useEffect(() => {
    if (reason) trackEvent('assistant_greeting_shown', { reason });
  }, [reason]);

  if (!reason) return null;

  const tutorialLink = (
    <a
      href={docsGuideLink(SIMPLE_CHAT_TUTORIAL_SLUG)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="assistant-greeting-tutorial"
      className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
    >
      Build the ready-made chat service (two minutes)
    </a>
  );

  return (
    <div
      data-testid="assistant-greeting"
      className="flex gap-2 text-sm text-gray-900 dark:text-gray-100"
    >
      <span className="shrink-0">✨</span>
      <div className="min-w-0 space-y-2 break-words">
        {reason === 'no_plan' ? (
          canManageBilling ? (
            <p>
              {hi} Your account has no plan yet. Choose one at Settings → Subscription and I will
              help you build your first AI Service.
            </p>
          ) : (
            <p>
              {hi} Your account has no plan yet. Ask your account owner to choose one, and I will
              help you build your first AI Service.
            </p>
          )
        ) : reason === 'free_no_service' ? (
          <p>
            {hi} Ask me anything about Monarch Works.{' '}
            You're on Free: describe what you want your first AI Service to do and I will assemble it.
          </p>
        ) : (
          <p>
            {hi} Describe what you want your first AI Service to do and I will assemble it, or
            ask me how anything works.
          </p>
        )}
        <p className="flex flex-wrap gap-x-4 gap-y-1">
          {/* The plan cards live at Settings → Subscription; Billing is invoices.
              Free accounts keep the link as an upgrade nudge. */}
          {(reason === 'no_plan' || reason === 'free_no_service') && canManageBilling && (
            <Link
              to="/settings/subscription"
              data-testid="assistant-greeting-choose-plan"
              className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Choose a plan
            </Link>
          )}
          {tutorialLink}
        </p>
      </div>
    </div>
  );
}
